import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress, Grid, Card, CardContent, Button, IconButton, Popover } from '@mui/material';
import { GetApp as ExportIcon } from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
} from 'chart.js';
import moment from 'moment-timezone';
import 'chartjs-adapter-moment';
import { addDays, subDays, format, parseISO } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { nl } from 'date-fns/locale';
import { useApi } from '../hooks/useApi';

// Registreer de benodigde Chart.js componenten
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

// Utility functies voor tijdconversie met moment.js
const UTC2CET = (date) => {
    return moment(date).tz('Europe/Amsterdam').toDate();
};

const CET2UTC = (date) => {
    return moment(date).tz('Europe/Amsterdam').utc().toDate();
};

const PriceDataList = ({ 
    activeChart = 'all', 
    selectedDate = new Date(),
    onDateChange,
    onPreviousDay,
    onNextDay
}) => {
    const [scheduleData, setScheduleData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [datePickerAnchor, setDatePickerAnchor] = useState(null);
    const [electricitySettings, setElectricitySettings] = useState({ show_all_in: true });
    const [hydrogenDisplaySettings, setHydrogenDisplaySettings] = useState({ 
        hydrogen_type: 'green', 
        show_retail: true 
    });
    const { get } = useApi();

    // Functie om de juiste waterstofprijs kolom te bepalen
    function getHydrogenPriceField() {
        const typePrefix = `hydrogen_${hydrogenDisplaySettings.hydrogen_type}`;
        const suffix = hydrogenDisplaySettings.show_retail ? '_retail_price' : '_price';
        return `${typePrefix}${suffix}`;
    }

    // Functie om het label voor de waterstofprijs te bepalen
    function getHydrogenPriceLabel() {
        const typeMapping = {
            'green': 'Groene',
            'grey': 'Grijze', 
            'blue': 'Blauwe'
        };
        const typeName = typeMapping[hydrogenDisplaySettings.hydrogen_type] || 'Groene';
        const priceType = hydrogenDisplaySettings.show_retail ? 'Retail' : 'Markt';
        return `${typeName} Waterstof ${priceType} Prijs (€/kg)`;
    }

    // Functie om de juiste stroomprijs te bepalen (kale prijs of all-in prijs)
    const getElectricityPrice = (data) => {
        if (electricitySettings.show_all_in) {
            // Gebruik all-in prijs direct uit database als beschikbaar
            if (data.price_all_in !== null && data.price_all_in !== undefined) {
                return data.price_all_in;
            }
            // Fallback: bereken all-in prijs als database veld leeg is
            const netbeheerderTarief = data.price_netbeheerder_tarief || electricitySettings.netbeheerder_tarief || 0.12;
            const leverancierTarief = data.price_leverancier_tarief || electricitySettings.leverancier_tarief || 0.08;
            return data.price + netbeheerderTarief + leverancierTarief;
        } else {
            // Gebruik kale prijs (originele price veld)
            return data.price;
        }
    };

    // Filter de data voor alleen de geselecteerde dag
    const filteredData = useMemo(() => {
        const dayStart = moment(selectedDate).tz('Europe/Amsterdam').startOf('day').valueOf();
        const dayEnd = moment(selectedDate).tz('Europe/Amsterdam').endOf('day').valueOf();

        return scheduleData.filter(block => {
            const blockStart = parseISO(block.validfrom).getTime();
            return blockStart >= dayStart && blockStart <= dayEnd;
        });
    }, [scheduleData, selectedDate]);

    // Filter waterstof data uit de schedule data
    const filteredHydrogenData = useMemo(() => {
        const priceField = getHydrogenPriceField();
        return filteredData.filter(block => 
            block[priceField] !== null && 
            block[priceField] !== undefined
        );
    }, [filteredData, hydrogenDisplaySettings]);

    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchData = async () => {
            if (!mounted) return;
            
            try {
                setLoading(true);
                
                // Converteer de geselecteerde datum naar UTC voor de API call
                const cetStart = UTC2CET(selectedDate);
                const validfrom = format(CET2UTC(subDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
                const validto = format(CET2UTC(addDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
                
                const scheduleResponse = await get(`/schedule?validfrom=${validfrom}&validto=${validto}`, {
                    signal: controller.signal
                });

                if (!mounted) return;

                if (scheduleResponse.data?.blocks?.length > 0) {
                    const convertedBlocks = scheduleResponse.data.blocks.map(block => ({
                        ...block,
                        validfrom: moment.utc(block.validfrom).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss'),
                        validto: moment.utc(block.validto).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss')
                    }));
                    setScheduleData(convertedBlocks);
                } else {
                    setScheduleData([]);
                }
            } catch (err) {
                if (!mounted) return;
                if (err.name === 'AbortError') return;
                setError({
                    type: 'error',
                    message: 'Fout bij ophalen gegevens',
                    details: err.message
                });
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            mounted = false;
            controller.abort();
        };
        }, [selectedDate]);

    // Haal electricity price settings op
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchElectricitySettings = async () => {
            try {
                const response = await get('/electricity-price/settings', { signal: controller.signal });
                if (mounted && response.data) {
                    setElectricitySettings(response.data);
                }
            } catch (err) {
                if (mounted && err.name !== 'AbortError') {
                    console.error('Fout bij ophalen electricity price settings:', err);
                    // Gebruik default settings bij fout
                    setElectricitySettings({ show_all_in: true });
                }
            }
        };

        fetchElectricitySettings();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, []);

    // Haal hydrogen display settings op
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchHydrogenDisplaySettings = async () => {
            try {
                const response = await get('/hydrogen-price/display-settings', { signal: controller.signal });
                if (mounted && response.data) {
                    setHydrogenDisplaySettings(response.data);
                }
            } catch (err) {
                if (mounted && err.name !== 'AbortError') {
                    console.error('Fout bij ophalen hydrogen display settings:', err);
                    // Gebruik default settings bij fout
                    setHydrogenDisplaySettings({ hydrogen_type: 'green', show_retail: true });
                }
            }
        };

        fetchHydrogenDisplaySettings();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, []);

    const handleDateClick = (event) => {
        setDatePickerAnchor(event.currentTarget);
    };

    const handleDatePickerClose = () => {
        setDatePickerAnchor(null);
    };

 

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
                <Typography color={error.type} variant="h6">
                    {error.message}
                </Typography>
                {error.details && (
                    <Typography color="text.secondary" variant="body2">
                        {error.details}
                    </Typography>
                )}
            </Box>
        );
    }

    const priceChartData = {
        labels: filteredData.map(d => moment(d.validfrom).tz('Europe/Amsterdam').toDate()),
        datasets: electricitySettings.show_all_in ? [
            {
                label: 'All-in Stroomprijs (€/kWh)',
                data: filteredData.map(d => getElectricityPrice(d)),
                borderColor: 'rgb(255, 99, 132)',
                tension: 0.1,
                fill: false
            }
        ] : [
            {
                label: 'Kale Stroomprijs (€/kWh)',
                data: filteredData.map(d => getElectricityPrice(d)),
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1,
                fill: false
            }
        ]
    };

    const co2ChartData = {
        labels: filteredData.map(d => moment(d.validfrom).tz('Europe/Amsterdam').toDate()),
        datasets: [
            {
                label: 'CO2 Emissiefactor (g/kWh)',
                data: filteredData.map(d => d.emissionfactor),
                borderColor: 'rgb(255, 99, 132)',
                tension: 0.1,
                fill: false
            }
        ]
    };

    const hydrogenChartData = {
        labels: filteredHydrogenData.map(d => moment(d.validfrom).tz('Europe/Amsterdam').toDate()),
        datasets: [
            {
                label: getHydrogenPriceLabel(),
                data: filteredHydrogenData.map(d => d[getHydrogenPriceField()]),
                borderColor: hydrogenDisplaySettings.hydrogen_type === 'green' ? 'rgb(34, 197, 94)' :
                           hydrogenDisplaySettings.hydrogen_type === 'grey' ? 'rgb(107, 114, 128)' :
                           'rgb(59, 130, 246)', // blue
                tension: 0.1,
                fill: false
            }
        ]
    };

    // Chart opties voor stroomprijs (kan negatief zijn)
    const priceChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    displayFormats: {
                        hour: 'HH:mm'
                    },
                    parser: 'YYYY-MM-DD HH:mm:ss',
                    tooltipFormat: 'DD-MM-YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Tijd'
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Stroomprijs (€/kWh)'
                },
                grid: {
                    color: (context) => {
                        if (context.tick.value === 0) {
                            return 'rgba(0, 0, 0, 0.5)';
                        }
                        return 'rgba(0, 0, 0, 0.1)';
                    },
                    lineWidth: (context) => {
                        if (context.tick.value === 0) {
                            return 2;
                        }
                        return 1;
                    }
                }
            }
        }
    };

    // Chart opties voor CO2 emissiefactor (altijd positief)
    const co2ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    displayFormats: {
                        hour: 'HH:mm'
                    },
                    parser: 'YYYY-MM-DD HH:mm:ss',
                    tooltipFormat: 'DD-MM-YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Tijd'
                }
            },
            y: {
                min: 0,
                title: {
                    display: true,
                    text: 'CO2 Emissiefactor (g/kWh)'
                }
            }
        }
    };

    // Chart opties voor waterstof prijs (altijd positief, start bij 0)
    const hydrogenChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    displayFormats: {
                        hour: 'HH:mm'
                    },
                    parser: 'YYYY-MM-DD HH:mm:ss',
                    tooltipFormat: 'DD-MM-YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Tijd'
                }
            },
            y: {
                min: 0,
                title: {
                    display: true,
                    text: 'Waterstofprijs (€/kg)'
                }
            }
        }
    };

    // Export functie voor CSV export
    const handleExport = () => {
        if (filteredData.length === 0) return;

        const hydrogenPriceField = getHydrogenPriceField();
        const hydrogenLabel = getHydrogenPriceLabel();

        const headers = electricitySettings.show_all_in 
            ? ['Tijd', 'All-in Stroomprijs (€/kWh)', 'CO2 Emissiefactor (g/kWh)', hydrogenLabel]
            : ['Tijd', 'Kale Stroomprijs (€/kWh)', 'CO2 Emissiefactor (g/kWh)', hydrogenLabel];
        
        const csvData = filteredData.map(item => {
            const baseData = [
                moment(item.validfrom).tz('Europe/Amsterdam').format('DD-MM-YYYY HH:mm'),
                getElectricityPrice(item)?.toFixed(4) || '',
                item.emissionfactor?.toFixed(0) || '',
                item[hydrogenPriceField]?.toFixed(2) || ''
            ];
            return baseData;
        });

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `prijsdata_${moment(selectedDate).format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Export knop */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<ExportIcon />}
                    onClick={handleExport}
                    disabled={filteredData.length === 0}
                >
                    Export CSV
                </Button>
            </Box>

            {/* Datum navigatie */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <IconButton 
                    onClick={onPreviousDay} 
                    color="primary"
                    disabled={selectedDate.getFullYear() <= 2024}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        mx: 2, 
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: 1
                        },
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        transition: 'background-color 0.2s ease'
                    }}
                    onClick={handleDateClick}
                >
                    {moment(selectedDate).tz('Europe/Amsterdam').format('dddd D MMMM YYYY')}
                </Typography>
                <IconButton 
                    onClick={onNextDay} 
                    color="primary"
                    disabled={selectedDate >= addDays(new Date(), 1)}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>

            {/* Date Picker Popover */}
            <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                    <DateCalendar
                        value={selectedDate}
                        onChange={(newDate) => {
                            onDateChange(newDate || new Date());
                            handleDatePickerClose();
                        }}
                        sx={{ 
                            '& .MuiDateCalendar-root': {
                                width: 'auto',
                                height: 'auto'
                            }
                        }}
                    />
                </LocalizationProvider>
            </Popover>

            <Grid container spacing={3}>
                {/* Toon alleen stroomprijzen grafiek */}
                {(activeChart === 'all' || activeChart === 'electricity') && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Stroomprijzen Slimverbruik.nl</Typography>
                                <Box sx={{ height: '400px', width: '100%' }}>
                                    {filteredData.length > 0 ? (
                                        <Line data={priceChartData} options={priceChartOptions} />
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Geen stroomprijs data beschikbaar voor deze dag
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Toon alleen CO2 grafiek */}
                {(activeChart === 'all' || activeChart === 'co2') && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>CO2 Emissiefactor</Typography>
                                <Box sx={{ height: '400px', width: '100%' }}>
                                    {filteredData.length > 0 ? (
                                        <Line data={co2ChartData} options={co2ChartOptions} />
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Geen CO2 emissiefactor data beschikbaar voor deze dag
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Toon alleen waterstof grafiek */}
                {(activeChart === 'all' || activeChart === 'hydrogen') && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>{getHydrogenPriceLabel()}</Typography>
                                <Box sx={{ height: '400px', width: '100%' }}>
                                    {filteredHydrogenData.length > 0 ? (
                                        <Line data={hydrogenChartData} options={hydrogenChartOptions} />
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Geen waterstof prijs data beschikbaar voor deze dag
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export { PriceDataList }; 